<template lang="pug">
BriqTable
  tr
    th
    th Price
    th(v-if="anySegment") Segment
    th Quantity
    th Total
  
  template(v-for="(dealCost, i) in bookingCosts.ProductGroups")
    tr.cursor-pointer(:key="`costs-deal-${i}`" @click="toggleViewProducts(i)" style="font-weight: 400")
      td
        .name-wrapper
          div
            .wrapped-name(:title="dealCost.DisplayName") {{ dealCost.DisplayName }}
            .wrapped-description(v-if="hasExtras(dealCost.BookingProducts)") {{ extraProductsLabel(dealCost.BookingProducts) }}
          .icon-wrapper(:class="{invert: dealIsExpanded(i)}")
            BriqIcon(name="icon-input-arrow-down-icon")
      td {{ formatPrice(dealCost.EstimatedPricePerPerson[priceField]) }}
      td(v-if="anySegment") 
      td {{ dealCost.Quantity }}
      td 
        span(v-show="!dealIsExpanded(i)") {{ formatPrice(dealCost.TotalPrice[priceField]) }}
    tr(
      v-show="dealIsExpanded(i)"
      v-for="(dealProductCost, j) in dealCost.BookingProducts"
      :key="`costs-deal-product-${i}-${j}`"
      style="font-weight: 300"
      )
      td(style="max-width:150px")
        WarningLabel(
          v-if="dealProductCost.ProductActiveState !== 'Active'"
          :label="dealProductCost.ProductName"
          :status="dealProductCost.ProductActiveState"
          style="margin-left: 22px"
          )
        .wrapped-name(v-else style="margin-left: 22px" :title="dealProductCost.ProductName") {{ dealProductCost.ProductName }}
        .wrapped-description(style="margin-left: 22px") {{ productType(dealProductCost.DealProductType) }}
      td {{ formatPrice(dealProductCost.EstimatedPricePerPerson[priceField]) }}
      td.wrapped-name(v-if="anySegment" :title="dealProductCost.SegmentName") {{ dealProductCost.SegmentName }}
      td {{ dealProductCost.Quantity }}
      td {{ formatPrice(dealProductCost.TotalPrice[priceField]) }}
  
  tr(v-for="(productCost, i) in bookingCosts.BookingProducts" :key="`costs-product-${i}`")
    td 
      WarningLabel(
        v-if="productCost.ProductActiveState !== 'Active'"
        :label="productCost.ProductName"
        :status="productCost.ProductActiveState"
        )
      .wrapped-name(v-else :title="productCost.ProductName") {{ productCost.ProductName }}
    td {{ formatPrice(productCost.EstimatedPricePerPerson[priceField]) }}
    td.wrapped-name(v-if="anySegment" :title="productCost.SegmentName") {{ productCost.SegmentName }}
    td {{ productCost.Quantity }}
    td {{ formatPrice(productCost.TotalPrice[priceField]) }}

  template(v-if="hasBookingFee")
    EmptyRow
    tr
      // Booking fee is sum of processor fee and merchant fee, provided processor fee is added on top of the booking (advanced settings)
      td Booking fee
      td
      td(v-if="anySegment")
      td
      td {{ formatPrice(bookingCosts.BookingFee[priceField]) }}

  EmptyRow

  tr
    td(:colspan="anySegment ? 4 : 3") Subtotal
    td {{ formatPrice(costsSubtotal) }}

  template(v-if="hasDiscountsOrVouchers")
    EmptyRow
    tr(v-for="(d, i) in bookingCosts.Discounts" :key="`discount-${i}`")
      td Discount
      td {{ discountLabel(d) }}
      td(v-if="anySegment")
      td
      td
        .action-buttons
          button.action-button(@click="editDiscount(d)")
            BriqIcon(name="icon-input-edit-icon")
          button.action-button(@click="removeDiscount(d.Key)")
            BriqIcon(name="icon-input-trash-icon")
    tr(v-for="(v, i) in bookingCosts.Vouchers" :key="`voucher-${i}`")
      td {{ v.VoucherProcessor }} : {{ v.VoucherCode }}
      td {{ voucherLabel(v) }}
      td(v-if="anySegment")
      td
      td
        .action-buttons
          button.action-button(@click="removeVoucher(v.Key)")
            BriqIcon(name="icon-input-trash-icon")
    tr
      td Subtotal discounts
      td
      td(v-if="anySegment")
      td
      td {{ formatPrice(subtotalDiscountsAndVouchers) }}

  EmptyRow

  tr
    td(:colspan="anySegment ? 4 : 3") Total (excl. tax)
    td {{ formatPrice(totalCostExcludingTax) }}

  tr(
    v-for="(tax, taxName) in totalTaxesByName"
    :key="`tax-${taxName}`"
    v-if="tax.Tax"
    )
    td(:colspan="anySegment ? 4 : 3") {{ taxName }} ({{ tax.TaxRate }}%)
    td {{ formatPrice(tax.Tax) }}

  tr.bold
    td(:colspan="anySegment ? 4 : 3") Total (incl. tax)
    td {{ formatPrice(totalCostIncludingTax) }}
    
</template>

<script>
import {
  includes,
  length,
  any,
  prop,
  propOr,
  chain,
  pipe,
  converge,
  concat,
  filter,
  propEq,
} from 'ramda'
import { mapGetters } from 'vuex'
import { currencyFormatter } from '@/helpers/intlFormatter.js'
import EmptyRow from './EmptyRow.vue'
import WarningLabel from '@v/components/WarningLabel.vue'
import DealProductType from '@/vue/bookings/edit/models/DealProductType.js'

export default {
  name: 'BookingCostsTable',
  components: { EmptyRow, WarningLabel },
  props: {
    bookingCosts: { type: Object, required: true },
    displayPricesIncludingTax: { type: Boolean, required: true },
  },
  data() {
    return {
      expandedDealIndices: [],
    }
  },

  computed: {
    ...mapGetters('bookingDetails', [
      'costsSubtotal',
      'subtotalDiscountsAndVouchers',
      'totalCostExcludingTax',
      'totalTaxesByName',
      'totalCostIncludingTax',
    ]),

    priceField() {
      return this.displayPricesIncludingTax ? 'IncludingTax' : 'ExcludingTax'
    },
    priceFormatter() {
      return currencyFormatter(this.$locale, this.$currency)
    },
    hasBookingFee() {
      return !!this.bookingCosts.BookingFee?.ExcludingTax
    },
    hasDiscountsOrVouchers() {
      return this.bookingCosts.Vouchers?.length || this.bookingCosts.Discounts?.length
    },
    anySegment() {
      return pipe(
        converge(concat, [
          propOr([], 'BookingProducts'),
          pipe(prop('ProductGroups'), chain(propOr([], 'BookingProducts'))),
        ]),
        any(prop('SegmentName')),
      )(this.bookingCosts)
    },
  },

  methods: {
    formatPrice(price) {
      return this.priceFormatter.format(price)
    },
    isPriceDistinctionGreaterThanTwoDecimal(bookingCostsTotalPrice, calculatedTotalPrice) {
      return Math.abs(bookingCostsTotalPrice - calculatedTotalPrice) > 0.01
    },
    isPriceDistinctionExists(bookingCostsTotalPrice, calculatedTotalPrice) {
      return this.formatPrice(bookingCostsTotalPrice) !== this.formatPrice(calculatedTotalPrice)
    },
    toggleViewProducts(dealIndex) {
      if (this.dealIsExpanded(dealIndex))
        this.expandedDealIndices = this.expandedDealIndices.filter(i => i !== dealIndex)
      else this.expandedDealIndices.push(dealIndex)
    },
    dealIsExpanded(dealIndex) {
      return includes(dealIndex)(this.expandedDealIndices)
    },
    discountLabel(discount) {
      return discount.Type === 'Percent'
        ? `- ${discount.Value}%`
        : `- ${this.formatPrice(discount.Value)}`
    },
    voucherLabel(voucher) {
      return voucher.VoucherDiscountPercentage
        ? `- ${voucher.VoucherDiscountPercentage}%`
        : `- ${this.formatPrice(voucher.CalculatedAmount)}`
    },
    editDiscount(discount) {
      this.$emit('editDiscount', discount)
    },
    removeDiscount(key) {
      this.$emit('removeDiscount', key)
    },
    removeVoucher(key) {
      this.$emit('removeVoucher', key)
    },
    productType(dealProductType) {
      switch (dealProductType) {
        case DealProductType.DealProduct:
          return 'Base product'
        case DealProductType.CrossSell:
          return 'Extra product'
        default:
          return ''
      }
    },
    extrasCount(dealBookingProducts) {
      return pipe(
        filter(propEq('DealProductType', DealProductType.CrossSell)),
        length,
      )(dealBookingProducts)
    },
    hasExtras(dealBookingProducts) {
      return this.extrasCount(dealBookingProducts) > 0
    },
    extraProductsLabel(dealBookingProducts) {
      return pipe(
        this.extrasCount,
        x => `+ ${x} extra product${x === 1 ? '' : 's'}`,
      )(dealBookingProducts)
    },
    logPriceDifferenceWarnings(totalPrice, calculatedTotalPrice, type) {
      this.$logWarning(`Total.${type}Tax`, totalPrice)
      this.$logWarning(`while calculated totalCost${type}Tax`, calculatedTotalPrice)
    },
    showWarningForPriceDifferences({
      notifyWarning,
      excludingIncludingTaxLabel,
      bookingCostsTotal,
      totalCostTax,
    }) {
      const checkPriceCondition = notifyWarning
        ? this.isPriceDistinctionGreaterThanTwoDecimal
        : this.isPriceDistinctionExists
      if (checkPriceCondition(bookingCostsTotal, totalCostTax)) {
        if (notifyWarning)
          this.$notifyWarning(
            `Total cost ${excludingIncludingTaxLabel.toLowerCase()} tax mismatch with backend, please notify support`,
          )
        this.logPriceDifferenceWarnings(
          this.bookingCosts.Total.ExcludingTax,
          this.totalCostExcludingTax,
          excludingIncludingTaxLabel,
        )
      }
    },
  },
  mounted() {
    this.showWarningForPriceDifferences({
      notifyWarning: true,
      excludingIncludingTaxLabel: 'Excluding',
      bookingCostsTotal: this.bookingCosts.Total.ExcludingTax,
      totalCostTax: this.totalCostExcludingTax,
    })
    this.showWarningForPriceDifferences({
      notifyWarning: true,
      excludingIncludingTaxLabel: 'Including',
      bookingCostsTotal: this.bookingCosts.Total.IncludingTax,
      totalCostTax: this.totalCostIncludingTax,
    })

    // if there is any real difference then print the difference in the console
    this.showWarningForPriceDifferences({
      notifyWarning: false,
      excludingIncludingTaxLabel: 'Including',
      bookingCostsTotal: this.bookingCosts.Total.ExcludingTax,
      totalCostTax: this.totalCostExcludingTax,
    })
    this.showWarningForPriceDifferences({
      notifyWarning: false,
      excludingIncludingTaxLabel: 'Including',
      bookingCostsTotal: this.bookingCosts.Total.IncludingTax,
      totalCostTax: this.totalCostIncludingTax,
    })
  },
}
</script>

<style scoped lang="stylus">
.name-wrapper
  display flex

.wrapped-name
  text-overflow ellipsis
  white-space nowrap
  overflow hidden
  max-width 500px

.wrapped-description
  font-size 12px
  line-height 16px
  color #4E5A6A

.icon-wrapper
  margin-left 8px
  opacity 0.5
  transition opacity 0.2s ease
  transition transform 0.2s ease
  display inline-flex
  align-items center

tr:hover
  .icon-wrapper
    opacity 1

.invert
  transform rotate(180deg)

.action-buttons
  display flex
  justify-content flex-end

.action-button
  margin 0 4px
  opacity 0.7
  transition opacity 0.2s ease
  &:hover
    opacity 1
</style>
